import React from 'react';

import type { Nullable, SimpleObject } from '@clearverify/types';

import { SignupVerifierFormStepId, verifierAdminFormFlow } from 'src/constants/auth';

import {
  AuthSignupVerifierContext,
  AuthSignupVerifierContextValue,
  FormStepperVerifierState,
} from './AuthSignupVerifierContext';

export const AuthSignupVerifierProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [formData, setFormData] = React.useState<SimpleObject>({});
  const [formRelatedPage, setFormRelatedPage] = React.useState<SimpleObject>({});

  const [formFlow, setFormFlow] = React.useState<SignupVerifierFormStepId[]>([
    SignupVerifierFormStepId.initial,
  ]);
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);

  const formStepperState = React.useMemo((): FormStepperVerifierState => {
    const nextStepId = formFlow[currentStepIndex + 1];

    return {
      previousStepId: formFlow[currentStepIndex - 1],
      currentStepId: formFlow[currentStepIndex] as string,
      nextStepId,
      isNextStepAvailable: Boolean(nextStepId),
      isLastStep: currentStepIndex === formFlow.length - 1,
    };
  }, [currentStepIndex, formFlow]);

  const goToNextStep = React.useCallback(() => {
    if (formStepperState.nextStepId) {
      setCurrentStepIndex(currentIndex => currentIndex + 1);
    }
  }, [formStepperState.nextStepId]);

  const goToStep = React.useCallback((nextStepId: SignupVerifierFormStepId) => {
    const stepIndex = verifierAdminFormFlow.findIndex(stepId => stepId === nextStepId);
    setCurrentStepIndex(stepIndex);
  }, []);

  const goToPreviousStep = React.useCallback(() => {
    if (formStepperState.previousStepId) {
      setCurrentStepIndex(currentIndex => currentIndex - 1);
    }
  }, [formStepperState.previousStepId]);

  React.useEffect(() => {
    setFormFlow(verifierAdminFormFlow);
  }, []);

  const updateFormRelatedPage = React.useCallback((formValues: Nullable<SimpleObject>) => {
    setFormRelatedPage(currentFormData => {
      return { ...currentFormData, ...formValues };
    });
  }, []);

  const updateFormData = React.useCallback((formValues: SimpleObject) => {
    setFormData(currentFormData => {
      return { ...currentFormData, ...formValues };
    });
  }, []);

  const contextValue: AuthSignupVerifierContextValue = React.useMemo(() => {
    return {
      formData,
      formRelatedPage,
      setFormData,
      updateFormRelatedPage,
      updateFormData,
      formStepperState,
      goToNextStep,
      goToStep,
      goToPreviousStep,
      setCurrentStepIndex,
      setFormFlow,
    };
  }, [
    formData,
    formRelatedPage,
    formStepperState,
    goToNextStep,
    goToPreviousStep,
    updateFormData,
    updateFormRelatedPage,
    goToStep,
  ]);

  return <AuthSignupVerifierContext value={contextValue}>{children}</AuthSignupVerifierContext>;
};

AuthSignupVerifierProvider.displayName = 'AuthSignupVerifierProvider';
